import {
  InProgressParams,
  InProgressResponse,
} from '@stripe-internal/conversations-client';
import {ApiMethods} from './ApiMethods';
import {ConversationsApiListConversationsEndpointRequest} from '../generated/types';
import {FrameworkAdapter} from './FrameworkAdapter';
import {
  isValidAudience,
  isValidEntryPoint,
  isValidTriggerPoint,
} from './validators';

type InProgressHandlerDeps = {
  listConversations: (
    params: Parameters<ApiMethods['listConversations']>[0],
  ) => ReturnType<ApiMethods['listConversations']>;
};

type InProgressValidationError = {
  code: 400;
  error: string;
};

export class InProgressHandler {
  private readonly deps: InProgressHandlerDeps;

  constructor(deps: InProgressHandlerDeps) {
    this.deps = deps;
  }

  call = async (params: InProgressParams): Promise<InProgressResponse> => {
    const request = InProgressHandler.buildListConversationsRequest(params);
    if ('error' in request) {
      return request;
    }

    const response = await this.deps.listConversations(request);

    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    const [conversation] = response.data;

    return {
      code: 200,
      data: {
        conversationId: conversation?.id,
        inProgress: Boolean(conversation?.is_active),
        channel: 'bot',
        clientType: 'conversations-api',
      },
    };
  };

  private static buildListConversationsRequest(
    params: InProgressParams,
  ):
    | ConversationsApiListConversationsEndpointRequest
    | InProgressValidationError {
    const {context} = params;

    if (!context?.audience) {
      return {code: 400, error: 'Missing audience'};
    }
    if (!isValidAudience(context.audience)) {
      return {code: 400, error: 'Invalid audience'};
    }

    if (!context.entryPoint) {
      return {code: 400, error: 'Missing entrypoint'};
    }
    if (!isValidEntryPoint(context.entryPoint)) {
      return {code: 400, error: 'Invalid entrypoint'};
    }

    const triggerPoint = context.triggerParams?.triggerPoint;

    return {
      audience: context.audience,
      entry_point: context.entryPoint,
      trigger_point:
        triggerPoint && isValidTriggerPoint(triggerPoint)
          ? triggerPoint
          : undefined,
      conversation_ids: params.conversationId
        ? [params.conversationId]
        : undefined,
      status: 'active',
      limit: 1,
    };
  }
}
